import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Clock, CheckCircle2, XCircle, LogIn, LogOut, ShieldOff } from 'lucide-react';
import StatusBoard from '../components/StatusBoard';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public time-clock punch screen. No auth, no sidebar. Persistent kiosk view.
 * Device whitelist enforced via localStorage token verified against backend on mount.
 */
export default function TimeClockPunch() {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {action, employee_name, time}
  const [error, setError] = useState('');
  const [now, setNow] = useState(new Date());
  const [authState, setAuthState] = useState({ checked: false, ok: false, deviceName: '' });
  const [boardRefresh, setBoardRefresh] = useState(0);
  const resetTimer = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Verify device on mount
  useEffect(() => {
    let active = true;
    const verify = async () => {
      const token = localStorage.getItem('fixif_clock_token');
      if (!token) {
        if (active) setAuthState({ checked: true, ok: false, deviceName: '' });
        return;
      }
      try {
        const r = await axios.post(`${API}/api/timeclock/verify-device`, { token });
        if (active) setAuthState({ checked: true, ok: true, deviceName: r.data.device_name });
      } catch {
        if (active) setAuthState({ checked: true, ok: false, deviceName: '' });
      }
    };
    verify();
    return () => { active = false; };
  }, []);

  const clearAfter = (ms = 5000) => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      setResult(null);
      setError('');
      setPin('');
    }, ms);
  };

  const submit = async (newPin) => {
    if (newPin.length !== 4 || busy) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const token = localStorage.getItem('fixif_clock_token') || '';
      const res = await axios.post(`${API}/api/timeclock/punch`, { pin: newPin, device_token: token });
      setResult(res.data);
      setPin('');
      setBoardRefresh(k => k + 1);
      clearAfter(4500);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al registrar';
      // If device gets revoked mid-session, kick back to unauthorized state
      if (err.response?.status === 403 && /Dispositivo/.test(detail)) {
        localStorage.removeItem('fixif_clock_token');
        setAuthState({ checked: true, ok: false, deviceName: '' });
      } else {
        setError(detail);
        setPin('');
        clearAfter(3500);
      }
    }
    setBusy(false);
  };

  const onPress = (k) => {
    if (busy) return;
    setError('');
    setResult(null);
    if (k === 'C') { setPin(''); return; }
    if (k === '<') { setPin(p => p.slice(0, -1)); return; }
    setPin(p => {
      if (p.length >= 4) return p;
      const next = p + k;
      if (next.length === 4) submit(next);
      return next;
    });
  };

  // Allow physical keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key >= '0' && e.key <= '9') onPress(e.key);
      else if (e.key === 'Backspace') onPress('<');
      else if (e.key === 'Escape') onPress('C');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, pin]);

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '<'];

  // Loading state
  if (!authState.checked) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#0F2349]">
        <div className="text-white/60 text-sm flex items-center gap-2">
          <Clock className="w-5 h-5 animate-spin" /> Verificando dispositivo...
        </div>
      </div>
    );
  }

  // Unauthorized state
  if (!authState.ok) {
    return <UnauthorizedScreen onRegistered={(name) => setAuthState({ checked: true, ok: true, deviceName: name })} />;
  }

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4"
      style={{
        background: 'radial-gradient(circle at 30% 20%, #2A4F90 0%, #1D3C6E 35%, #0F2349 100%)',
      }}
      data-testid="timeclock-punch-screen"
    >
      <div className="w-full max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
        {/* Left: keypad column */}
        <div className="w-full max-w-md mx-auto">
        {/* Brand + clock */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-4">
            <div className="w-2 h-2 rounded-full bg-[#AFBD20] animate-pulse" />
            <span className="text-xs uppercase tracking-widest text-white/70 font-semibold">Fixif Checador</span>
            {authState.deviceName && <span className="text-xs text-white/50 ml-1">· {authState.deviceName}</span>}
          </div>
          <div className="text-white text-5xl font-bold font-mono tabular-nums tracking-wider">
            {now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
          </div>
          <div className="text-white/60 text-sm mt-1 capitalize">
            {now.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>

        {/* Result / Error / Prompt */}
        {result ? (
          <div
            className={`rounded-2xl p-6 text-center mb-6 border backdrop-blur-sm ${
              result.action === 'in'
                ? 'bg-emerald-500/15 border-emerald-400/40'
                : 'bg-amber-500/15 border-amber-400/40'
            }`}
            data-testid="timeclock-result"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              {result.action === 'in' ? (
                <LogIn className="w-6 h-6 text-emerald-300" />
              ) : (
                <LogOut className="w-6 h-6 text-amber-300" />
              )}
              <span className={`text-sm font-semibold uppercase tracking-widest ${result.action === 'in' ? 'text-emerald-300' : 'text-amber-300'}`}>
                {result.action === 'in' ? 'Entrada registrada' : 'Salida registrada'}
              </span>
            </div>
            <p className="text-3xl font-bold text-white">{result.employee_name}</p>
            <p className="text-xs text-white/60 mt-2 font-mono">
              {new Date(result.time).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
          </div>
        ) : error ? (
          <div className="rounded-2xl p-5 text-center mb-6 border bg-rose-500/15 border-rose-400/40 backdrop-blur-sm" data-testid="timeclock-error">
            <XCircle className="w-7 h-7 text-rose-300 mx-auto mb-1" />
            <p className="text-rose-100 text-sm font-medium">{error}</p>
          </div>
        ) : (
          <div className="rounded-2xl p-5 text-center mb-6 bg-white/5 border border-white/10">
            <p className="text-white/70 text-sm">Ingresa tu PIN de 4 digitos</p>
          </div>
        )}

        {/* PIN dots */}
        <div className="flex items-center justify-center gap-3 mb-6" data-testid="timeclock-pin-dots">
          {[0, 1, 2, 3].map(i => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                i < pin.length ? 'bg-[#AFBD20] border-[#AFBD20] scale-110' : 'border-white/30'
              }`}
            />
          ))}
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3" data-testid="timeclock-keypad">
          {keys.map(k => {
            const isAction = k === 'C' || k === '<';
            return (
              <button
                key={k}
                onClick={() => onPress(k)}
                disabled={busy}
                data-testid={`timeclock-key-${k === '<' ? 'back' : k === 'C' ? 'clear' : k}`}
                className={`h-16 rounded-xl text-2xl font-semibold transition-all duration-150 active:scale-95 ${
                  isAction
                    ? 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                    : 'bg-white/10 border border-white/15 text-white hover:bg-white/20 hover:border-[#AFBD20]/40 backdrop-blur-sm'
                } ${busy ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {k === '<' ? '←' : k}
              </button>
            );
          })}
        </div>

        {busy && (
          <div className="flex items-center justify-center gap-2 mt-6 text-white/60 text-sm">
            <Clock className="w-4 h-4 animate-spin" /> Procesando...
          </div>
        )}

        <div className="mt-10 text-center">
          <a href="/login" className="text-xs text-white/40 hover:text-white/70 transition-colors">
            Acceso administrativo
          </a>
        </div>
        </div>
        {/* Right: status board */}
        <div className="w-full lg:sticky lg:top-4">
          <StatusBoard variant="kiosk" pollMs={8000} refreshKey={boardRefresh} />
        </div>
      </div>
    </div>
  );
}

function UnauthorizedScreen({ onRegistered }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!name.trim()) { setError('Ingresa un nombre para el dispositivo'); return; }
    if (pin.length !== 6) { setError('El PIN debe ser de 6 digitos'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/timeclock/devices/register-with-pin`, { name: name.trim(), admin_pin: pin });
      localStorage.setItem('fixif_clock_token', r.data.token);
      onRegistered(r.data.name);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar');
    }
    setBusy(false);
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center"
      style={{ background: 'radial-gradient(circle at 30% 20%, #4a1818 0%, #2a0e0e 50%, #150505 100%)' }}
      data-testid="timeclock-unauthorized"
    >
      <div className="max-w-md mx-auto px-6 text-center w-full">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-rose-500/20 border border-rose-400/40 mb-6">
          <ShieldOff className="w-10 h-10 text-rose-300" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Dispositivo no autorizado</h1>
        <p className="text-rose-200/80 text-sm mb-6">
          Este dispositivo no esta registrado para usar el checador.
        </p>

        {!showForm ? (
          <div className="space-y-3">
            <button
              onClick={() => setShowForm(true)}
              className="w-full px-4 py-3 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white font-medium transition-all"
              data-testid="register-with-pin-btn"
            >
              Registrar este dispositivo
            </button>
            <p className="text-xs text-rose-200/60">
              O solicita a un administrador que lo registre desde Configuracion → Dispositivos Checador.
            </p>
            <a href="/login" className="inline-block text-xs text-white/50 hover:text-white/80 underline transition-colors mt-4">
              Acceso administrativo
            </a>
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-3 backdrop-blur-sm text-left">
            <p className="text-xs uppercase tracking-widest text-white/60 text-center">Registro con PIN admin</p>
            <div>
              <label className="text-xs text-white/60 block mb-1">Nombre del dispositivo</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Tablet Recepcion"
                data-testid="device-register-name"
                className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#AFBD20]/50"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">PIN de administrador (6 digitos)</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                data-testid="device-register-pin"
                className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-white text-center font-mono tracking-[0.4em] text-xl placeholder:text-white/30 focus:outline-none focus:border-[#AFBD20]/50"
              />
            </div>
            {error && <p className="text-sm text-rose-300 text-center" data-testid="register-error">{error}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => { setShowForm(false); setName(''); setPin(''); setError(''); }}
                className="flex-1 px-4 py-2 rounded-lg bg-transparent border border-white/15 text-white/70 hover:bg-white/5 text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={submit}
                disabled={busy}
                data-testid="device-register-submit"
                className="flex-1 px-4 py-2 rounded-lg bg-[#AFBD20] hover:bg-[#9CA819] disabled:opacity-50 text-slate-900 font-semibold text-sm"
              >
                {busy ? 'Registrando...' : 'Registrar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

